import {MdbsCore} from "@/api/tsclient";
import {deepCopy} from "@/auxillary";
import {v4 as uuidv4} from 'uuid';
import {translate} from "@/auxillary/translation";
import InstrumentContextCache from "@/models/instrument.context.cache";
import {VisitState} from "@/auxillary/visit.states";
import {resolveConstraintExpression} from "@/auxillary/constraint.expression.resolver";
import VisitContextDto = MdbsCore.VisitContextDto;
import DataFieldShortDto = MdbsCore.DataFieldShortDto;
import VisitDefinitionDto = MdbsCore.VisitDefinitionDto;
import ISignatureDataDto = MdbsCore.ISignatureDataDto;
import RuleDefinitionDto = MdbsCore.RuleDefinitionDto;
import {DEFINITIONTYPE_EDITABILITY, DEFINITIONTYPE_VISIBILITY} from "@/auxillary/rules";

export const DEFINITIONTYPE_GENERICVISIT: string = "GenericVisit";
export const DEFINITIONTYPE_INTERMEDIATEVISIT: string = "IntermediateVisit";

export default class VisitContextCache {

    private readonly _definition: Readonly<VisitDefinitionDto>;
    private readonly visitContext: Readonly<VisitContextDto> | undefined;
    private readonly _displayName?: string;
    private readonly _isDeleted: boolean = false;
    private readonly _order: number = 0;
    private readonly _temporaryId: string = "";
    private readonly _isLocked: boolean = false;
    private readonly _fields: Readonly<DataFieldShortDto>[] = [];
    private readonly _tags: any = {};
    private readonly _instruments: Array<Readonly<InstrumentContextCache>> = []
    private readonly _state: string = "";
    private readonly _isSigned: boolean = false;
    private readonly _signatureData: Readonly<ISignatureDataDto> | undefined;
    private readonly _visibilityRule: Readonly<RuleDefinitionDto> | undefined;
    private readonly _editabilityRules:  Array<Readonly<RuleDefinitionDto>> | undefined;
    private _brokenEditabilityRule:  RuleDefinitionDto | undefined;

    constructor(definition: MdbsCore.VisitDefinitionDto, visit: Readonly<MdbsCore.VisitContextDto> | undefined,
                instrumentContexts: Array<Readonly<InstrumentContextCache>>) {
        this._definition = Object.freeze(definition);
        this._displayName = translate(this.displayKey);
        this._visibilityRule = this.definition?.rules?.find(r => r.definitionType == DEFINITIONTYPE_VISIBILITY && !r.isDeleted)
        this._editabilityRules = this.definition?.rules?.filter(r => r.definitionType == DEFINITIONTYPE_EDITABILITY && !r.isDeleted)

        if (visit === undefined) {
            this.visitContext = undefined;
            this._order = definition.order ?? 0;
            this._temporaryId = uuidv4().toString();
            return;
        }

        this.visitContext = visit;
        this._instruments = instrumentContexts;
        this._isLocked = visit.locked ?? false;
        this._fields = deepCopy(visit.fields ?? [])
        this._tags = deepCopy(visit.tags)
        this._order = visit.order ?? 0;
        this._isDeleted = visit.isDeleted ?? false;
        this._state = visit.state ?? "";
        this._isSigned = visit.isSigned ?? false;
        this._signatureData = deepCopy(visit.signatureData);
    }

    public get instruments() {
        return this._instruments;
    }

    public get visibleInstruments() {
        return this._instruments.filter(i => i.isVisible);
    }

    public get id() {
        return this.visitContext?.id ?? this._temporaryId;
    }

    public get identifier() {
        return this._definition.identifier ?? "";
    }

    public get displayKey() {
        return this._definition.displayKey ?? "";
    }

    public get displayName() {
        return this._displayName;
    }

    public get state() {
        return this._state ?? "";
    }

    get definition() {
        return this._definition;
    }

    public get isNew() {
        return this.visitContext === undefined;
    }

    public get isNotDone() {
        return this._state === VisitState.NOT_DONE;
    }

    public get visitDate() {
        return this.visitContext?.visitDate;
    }

    get isDeleted(): boolean {
        return this._isDeleted;
    }

    get order(): number {
        return this._order;
    }

    get temporaryId(): string {
        return this._temporaryId;
    }

    get isVisible() {
        if (this.isDeleted || this._definition.isDeleted)
            return false;

        if (this._visibilityRule !== undefined) {
            return resolveConstraintExpression(this._visibilityRule.constraintExpression, undefined, this);
        }
        return true;
    }

    get isEditable() {
        this._brokenEditabilityRule = undefined;
        if (this.isDeleted || this._definition.isDeleted)
            return false;

        if (this._editabilityRules === undefined || this._editabilityRules.length == 0)
            return true;

        const brokenRules = this._editabilityRules.filter(rule =>
            !resolveConstraintExpression(rule.constraintExpression, undefined, this));

        if (brokenRules.length == 0)
            return true;

        this._brokenEditabilityRule = brokenRules[0];
        return false;
    }

    get brokenEditableRule() {
        return this._brokenEditabilityRule;
    }

    get notEditableReason() {
        if (!this._brokenEditabilityRule)
            return "";
        return translate(this._brokenEditabilityRule?.displayKey);
    }

    get isGenericVisit() {
        return this.definition.definitionType == DEFINITIONTYPE_GENERICVISIT;
    }

    get isIntermediateVisit() {
        return this.definition.definitionType == DEFINITIONTYPE_INTERMEDIATEVISIT;
    }

    get isLocked(): boolean {
        return this._isLocked;
    }

    get fields(): DataFieldShortDto[] {
        return this._fields;
    }

    get tags(): any {
        return this._tags;
    }

    get isSigned(): boolean {
        return this._isSigned;
    }

    get signatureData() {
        return this._signatureData;
    }
}
